import axios from 'axios';

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function normalizeMessage(value: unknown): string | undefined {
    if (typeof value === 'string') {
        const message = value.trim();
        return message.length > 0 ? message : undefined;
    }

    if (!Array.isArray(value)) return undefined;

    const message = value
        .filter((entry): entry is string => typeof entry === 'string')
        .map((entry) => entry.trim())
        .filter(Boolean)
        .join('\n');

    return message.length > 0 ? message : undefined;
}

export function extractErrorMessage(error: unknown): string | undefined {
    if (axios.isAxiosError(error)) {
        const response = error.response as { data?: unknown; message?: unknown } | undefined;
        const payload = isRecord(response?.data) ? response.data : undefined;

        return (
            normalizeMessage(payload?.message) || // Proven: organization-api duplicate invite returns response.data.message.
            normalizeMessage(payload?.detail) || // Proven: incident error payloads/tests use response.data.detail.
            normalizeMessage(payload?.description) || // Not proven from a concrete backend response in this repo; kept because older incident handling already expected it.
            normalizeMessage(response?.message) || // Not proven for Axios responses here; kept defensively because older middleware tried to read error.response.message.
            normalizeMessage(error.message) // Proven: Axios and plain JS errors expose error.message.
        );
    }

    if (isRecord(error)) {
        const response = isRecord(error.response) ? error.response : undefined;
        const payload = isRecord(response?.data) ? response.data : undefined;

        return (
            normalizeMessage(payload?.message) || // Proven: organization-api duplicate invite returns response.data.message.
            normalizeMessage(payload?.detail) || // Proven: incident error payloads/tests use response.data.detail.
            normalizeMessage(payload?.description) || // Not proven from a concrete backend response in this repo; kept because older incident handling already expected it.
            normalizeMessage(response?.message) || // Not proven for wrapped error.response objects here; kept defensively for compatibility.
            normalizeMessage(error.message) // Proven: plain thrown errors commonly expose error.message.
        );
    }

    return undefined;
}

export type HttpLoadError = {
    kind: 'unavailable' | 'transient';
    title: string;
    description: string;
};

export function classifyHttpError(error: unknown, defaultTitle = 'Could not load data'): HttpLoadError {
    if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 404) {
            return {
                kind: 'unavailable',
                title: 'Service unavailable',
                description: 'Please contact your administrator.',
            };
        }
        if (status !== undefined && status >= 500) {
            return {
                kind: 'unavailable',
                title: 'Server error',
                description: 'Please contact your administrator.',
            };
        }
    }
    return {
        kind: 'transient',
        title: defaultTitle,
        description: getDefaultErrorMessage(error),
    };
}

export function getDefaultErrorMessage(error: unknown): string {
    if (axios.isAxiosError(error)) {
        const status = error.response?.status;

        if (status === 400) return extractErrorMessage(error) ?? 'Request was invalid';
        if (status === 401 || status === 403) return 'You dont have access to this request';
        if (status && status >= 500) return 'Server error - Please try again';
        if (!status) return 'Network error - Please check your connection';
    }

    return extractErrorMessage(error) ?? 'Something went wrong.';
}
